import React from "react";
import { Button } from '@/components/ui/button';
import { User2, History, Info } from 'lucide-react';
import { Label } from '@/components/ui/label';
import { AutoComplete } from '@/components/ui/autocomplete';
import type { Option } from '@/components/ui/autocomplete';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useNavigate } from "@tanstack/react-router";
import { Supplier } from "@/gen/suppliers/common_pb";
import CreatePurchaseOrderComponent from '@/components/purchase-order/create';
import {Route as createPurchaseOrderRoute} from '@/routes/(pharma)/_layout/inventory/create-purchase-order/index';
import { toast } from "@/components/ui/use-toast";
import { CreatePurchaseOrderRequest } from "@/gen/purchase_order/create_purchase_order_pb";
import { PurchaseOrderItem } from "@/gen/purchase_order/common_pb";
import { ItemType } from "@/gen/inventory/common_pb";
import { pharmacyClient } from "@/connect";

export default function CreatePurchaseOrder() {
    const navigate = useNavigate();
    const data = createPurchaseOrderRoute.useLoaderData();
    const [selectedSupplier, setSelectedSupplier] = React.useState<Option | undefined>(undefined)
    const [input, setInput] = React.useState("")
    const [isLoading, setIsLoading] = React.useState(false)
    const [rows, setRows] = React.useState<Array<{
        product: Option | undefined;
        bonus: string;
        quantity: string;
        intendedCost: string;
        productInput: string;
        notes?: string;
        purchaseRequestId?: string[];
    }>>([
        { product: undefined, bonus: '', quantity: '', intendedCost: '', productInput: '' }
    ])
    const bonusRefs = React.useRef<(HTMLInputElement | null)[]>([])

    const handleProductChange = (index: number, value: Option) => {
        setRows(prevRows => {
            const updated = [...prevRows]
            updated[index] = {
                ...updated[index],
                product: value,
                productInput: value.label,
                bonus: '',
                quantity: '',
                intendedCost: '',
            }
            if (index === prevRows.length - 1) {
                updated.push({ product: undefined, bonus: '', quantity: '', intendedCost: '', productInput: '' })
            }
            return updated
        })
        setTimeout(() => {
            bonusRefs.current[index]?.focus();
        }, 0);
    }

    const handleBulkAdd = (items: Array<{
        product: Option | undefined;
        bonus: string;
        quantity: string;
        intendedCost: string;
        productInput: string;
        notes?: string;
        purchaseRequestId?: string[];
    }>) => {
        // Track using purchaseRequestId. If the items are removed, then remove using the purchaseRequestId. If they are added, then add using the purchaseRequestId.
        setRows(prevRows => {
            const updated = [...prevRows]
            
            // Remove the last empty row if it exists
            if (updated.length > 0 && updated[updated.length - 1].product === undefined) {
                updated.pop();
            }
            // If any item is present in the rows with purchaseRequestId but is not in the items, remove it.
            const existingProductWithPurchaseRequestId = updated.filter(row => row.purchaseRequestId !== undefined);
            // Get the product index in the rows
            existingProductWithPurchaseRequestId.forEach(row => {
                const existingIndex = items.findIndex(item => item.purchaseRequestId === row.purchaseRequestId && item.product?.value === row.product?.value);
                if (existingIndex === -1) {
                    // Remove the row if it is not in the items
                    const indexToRemove = updated.findIndex(r => r.purchaseRequestId === row.purchaseRequestId && r.product?.value === row.product?.value);
                    if (indexToRemove !== -1) {
                        updated.splice(indexToRemove, 1);
                    }
                }
            })

            // Iterate through the items and update or add them. They have both the product and purchaseRequestId.
            // If the product already exists in the updated rows, update it. Otherwise, add it.
            items.forEach(item => {
                const existingIndex = updated.findIndex(row => row.purchaseRequestId === item.purchaseRequestId && row.product?.value === item.product?.value);
                if (existingIndex !== -1) {
                    // Update existing row
                    updated[existingIndex] = {
                        ...updated[existingIndex],
                        product: item.product,
                        bonus: item.bonus,
                        quantity: item.quantity,
                        intendedCost: item.intendedCost,
                        productInput: item.productInput,
                        notes: item.notes,
                        purchaseRequestId: item.purchaseRequestId
                    }
                } else {
                    // Add new row
                    updated.push({
                        product: item.product,
                        bonus: item.bonus,
                        quantity: item.quantity,
                        intendedCost: item.intendedCost,
                        productInput: item.productInput,
                        notes: item.notes,
                        purchaseRequestId: item.purchaseRequestId
                    });
                }
            });


            updated.push({ product: undefined, bonus: '', quantity: '', intendedCost: '', productInput: '' });
            return updated;
        });
        // Clear the input field
        setInput("");
    }

    const handleRowChange = (index: number, field: string, value: string) => {
        setRows(prevRows => {
            const updated = [...prevRows]
            updated[index] = {
                ...updated[index],
                [field]: value
            }
            return updated
        })
    }

    const handleClearRow = (index: number) => {
        setRows(prevRows => prevRows.filter((_, i) => i !== index));
    };

    const handleSubmit = async () => {

        // Validate the selected supplier
        if (!selectedSupplier) {
            toast({
                title: "Error",
                description: "Please select a supplier before submitting.",
                variant: "destructive",
                duration: 3000,
            })
            return;
        }

        // Validate the rows ignoring the last empty row
        const rowsToValidate = rows.slice(0, -1);
        if (rowsToValidate.length === 0) {
            toast({
                title: "Error",
                description: "Please add at least one item to the purchase order.",
                variant: "destructive",
                duration: 3000,
            })
            return;
        }
        for (const row of rowsToValidate) {
            if (!row.product || !row.quantity || !row.intendedCost) {
                toast({
                    title: "Error",
                    description: "Please fill in all required fields for each row.",
                    variant: "destructive",
                    duration: 3000,
                })
                return;
            }
        }

        try {
            // Prepare the purchase order data
            setIsLoading(true);
            const purchaseOrderData = new CreatePurchaseOrderRequest();
            purchaseOrderData.supplierId = selectedSupplier.value;

            let purchaseRequestIds = rowsToValidate.map(row => row.purchaseRequestId);
            // Unique purchaseRequestIds
            const finalpurchaseRequestIds = Array.from(new Set(purchaseRequestIds.flat()));
            if (purchaseRequestIds.length > 0) {
                // @ts-ignore
                purchaseOrderData.purchaseRequestIds = finalpurchaseRequestIds.filter(id => id !== undefined).flat();
            }  
            const items: PurchaseOrderItem[] = []
            for (const row of rowsToValidate) {
                if (!row.product || !row.quantity || !row.intendedCost) {
                    toast({
                        title: "Error",
                        description: "Please fill in all required fields for each row.",
                        variant: "destructive",
                        duration: 3000,
                    })
                    return;
                }
                const item = new PurchaseOrderItem();
                item.bonusQuantity = parseInt(row.bonus)
                item.itemType = row.product.type === ItemType.MEDICAL.toString() ? ItemType.MEDICAL : ItemType.NON_MEDICAL;
                item.masterId = row.product.value;
                item.quantity = parseInt(row.quantity);
                item.unitPrice = parseFloat(row.intendedCost);
                item.notes = row.notes || "";
                items.push(item);
            }
            purchaseOrderData.items = items;

             if (data.stockPointId) {
                purchaseOrderData.stockPointId = data.stockPointId;
            }
            // Send the purchase order request
            await pharmacyClient.createPurchaseOrder(purchaseOrderData)
            setIsLoading(false);
            toast({
                title: "Success",
                description: "Purchase order created successfully.",
                variant: "success",
                duration: 3000,
            });
            // Clear the form
            setRows([{ product: undefined, bonus: '', quantity: '', intendedCost: '', productInput: '' }]);
            setSelectedSupplier(undefined);
            setInput("");
            // TODO: Show New PDF here
        } catch (error) {
            console.error("Error creating purchase order:", error);
            toast({
                title: "Error",
                description: "Failed to create purchase order. Please try again.",
                variant: "destructive",
                duration: 3000,
            });
            setIsLoading(false);
        }
    }

    return (
        <div className="h-full w-full max-w-screen flex flex-col overflow-hidden">
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <BreadcrumbLink
                            onClick={() => {
                                navigate({
                                    to: "/inventory",
                                });
                            }}
                            style={{ cursor: "pointer" }}
                        >
                            Inventory Overview
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage>Create Purchase Order</BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>
            <hr className='mt-4' />
            <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-2 min-w-[320px]">
                    <Label htmlFor="supplier-select">Supplier:</Label>
                    <div className="w-[320px]">
                        <AutoComplete
                            options={data.suppliers.map((supplier: Supplier) => ({
                                value: supplier.id,
                                label: supplier.name,
                            }))}
                            value={selectedSupplier}
                            onValueChange={setSelectedSupplier}
                            input={input}
                            onInputChanged={setInput}
                            emptyMessage="No suppliers found."
                            placeholder="Select supplier"
                            isLoading={false}
                            disabled={false}
                        />
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline">
                        <User2 className="w-3 h-3 mr-1" />Change Supplier
                    </Button>
                    <Button size="sm" variant="outline">
                        <History className="w-3 h-3 mr-1" />View Past Orders
                    </Button>
                    <Button size="sm" variant="outline">
                        <Info className="w-3 h-3 mr-1" />View Supplier Info
                    </Button>
                </div>
            </div>
            <CreatePurchaseOrderComponent
                rows={rows}
                bonusRefs={bonusRefs}
                handleProductChange={handleProductChange}
                handleRowChange={handleRowChange}
                handleClearRow={handleClearRow}
                rowsPerPage={7}
                bordersTrue={true}
                handleSubmit={handleSubmit}
                loading={isLoading}
                purchaseRequests={data.purchaseRequests}
                handleBulkAdd={handleBulkAdd}
            />
        </div>
    )
}
